import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { darePackList } from '../data/darePacks';
import type { DarePack } from '../data/darePacks';
import { useDoOrDrinkStore } from '../stores/doOrDrinkStore';

function DoOrDrinkSetup() {
  const navigate = useNavigate();
  const initGame = useDoOrDrinkStore((s) => s.initGame);

  const [playerNames, setPlayerNames] = useState<string[]>(['', '']);
  const [darePack, setDarePack] = useState<DarePack['id']>('default');
  const [targetDares, setTargetDares] = useState(10);
  const [error, setError] = useState('');

  const addPlayer = () => {
    if (playerNames.length < 20) {
      setPlayerNames([...playerNames, '']);
    }
  };

  const removePlayer = (index: number) => {
    if (playerNames.length > 2) {
      setPlayerNames(playerNames.filter((_, i) => i !== index));
    }
  };

  const updatePlayer = (index: number, name: string) => {
    const updated = [...playerNames];
    updated[index] = name;
    setPlayerNames(updated);
  };

  const handleStart = () => {
    const trimmed = playerNames.map((n) => n.trim()).filter((n) => n.length > 0);

    if (trimmed.length < 2) {
      setError('Need at least 2 players');
      return;
    }

    const uniqueNames = new Set(trimmed.map((n) => n.toLowerCase()));
    if (uniqueNames.size !== trimmed.length) {
      setError('Player names must be unique');
      return;
    }

    setError('');
    initGame(trimmed, { targetDares, darePack });
    navigate('/doOrDrink/play');
  };

  const validPlayers = playerNames.filter((n) => n.trim().length > 0);
  const canStart = validPlayers.length >= 2;

  return (
    <div className="min-h-screen flex items-center justify-center bg-base-100 px-4 py-8">
      <div className="max-w-md w-full">
        <button onClick={() => navigate('/')} className="btn btn-ghost btn-sm mb-6">
          &larr; Back
        </button>

        <h1 className="text-3xl font-bold mb-2">Do or Drink</h1>
        <p className="text-base-content/60 mb-8">Set up your game</p>

        <div className="space-y-6">
          {/* Player Names */}
          <div className="form-control">
            <label className="label">
              <span className="label-text text-lg">Players</span>
              <span className="label-text-alt">{validPlayers.length} players</span>
            </label>
            <div className="space-y-2">
              {playerNames.map((name, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    type="text"
                    placeholder={`Player ${i + 1}`}
                    value={name}
                    onChange={(e) => updatePlayer(i, e.target.value)}
                    className="input input-bordered flex-1"
                    maxLength={20}
                  />
                  {playerNames.length > 2 && (
                    <button
                      onClick={() => removePlayer(i)}
                      className="btn btn-ghost btn-square"
                      aria-label="Remove player"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
            {playerNames.length < 20 && (
              <button onClick={addPlayer} className="btn btn-ghost btn-sm mt-2">
                + Add Player
              </button>
            )}
          </div>

          {/* Dare Pack Selection */}
          <div className="form-control">
            <label className="label">
              <span className="label-text text-lg">Dare Pack</span>
            </label>
            <div className="grid grid-cols-3 gap-2">
              {darePackList.map((pack) => (
                <button
                  key={pack.id}
                  onClick={() => setDarePack(pack.id)}
                  className={`btn btn-sm ${
                    darePack === pack.id ? 'btn-primary' : 'btn-ghost border-base-300'
                  }`}
                >
                  {pack.name}
                </button>
              ))}
            </div>
            <p className="text-base-content/50 text-sm mt-1">
              {darePackList.find((p) => p.id === darePack)?.description}
            </p>
          </div>

          {/* Target Dares */}
          <div className="form-control">
            <label className="label">
              <span className="label-text">Dares to Win</span>
              <span className="label-text-alt text-lg font-bold">{targetDares}</span>
            </label>
            <input
              type="range"
              min={3}
              max={50}
              value={targetDares}
              onChange={(e) => setTargetDares(Number(e.target.value))}
              className="range range-primary range-sm"
            />
            <div className="flex justify-between text-xs text-base-content/40 px-1 mt-1">
              <span>3</span>
              <span>50</span>
            </div>
          </div>

          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
            </div>
          )}

          <button
            onClick={handleStart}
            disabled={!canStart}
            className="btn btn-primary btn-lg w-full"
          >
            Start Game
          </button>
        </div>
      </div>
    </div>
  );
}

export default DoOrDrinkSetup;
